import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AttentionRuleId } from '@uniticket/shared';

// "Points d'attention" UI — the aggregated special-case badge (validation window only, user decision
// 2026-07-28: one amber style, no severity levels, never shown in the queue). The badge carries the
// fired-rule COUNT; hovering lists the rule titles; the apartment sheet renders <AttentionDetails>
// with the full explanations. Rule evaluation lives in @uniticket/shared (computeAttentionPoints).

/** Compact "⚠ N" chip; native tooltip = the fired rules' titles. Renders nothing when no rule fires. */
export function AttentionBadge({ ruleIds, className }: { ruleIds: AttentionRuleId[]; className?: string }) {
  const { t } = useTranslation(['tickets']);
  if (ruleIds.length === 0) return null;
  const titles = ruleIds.map((id) => `• ${t(`tickets:validation.attention.rules.${id}.title`)}`).join('\n');
  return (
    <span
      title={`${t('tickets:validation.attention.title')}\n${titles}`}
      aria-label={t('tickets:validation.attention.title')}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700',
        className,
      )}
    >
      <AlertTriangle className="h-3 w-3" />
      {ruleIds.length}
    </span>
  );
}

/** Full explanations, one block per fired rule — shown in the apartment detail sheet. */
export function AttentionDetails({ ruleIds }: { ruleIds: AttentionRuleId[] }) {
  const { t } = useTranslation(['tickets']);
  if (ruleIds.length === 0) return null;
  return (
    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
      <p className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-amber-800">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
        {t('tickets:validation.attention.title')}
      </p>
      <ul className="space-y-1.5">
        {ruleIds.map((id) => (
          <li key={id} className="text-[11px] leading-snug text-amber-800">
            <span className="font-semibold">{t(`tickets:validation.attention.rules.${id}.title`)}</span>
            {' — '}
            {t(`tickets:validation.attention.rules.${id}.detail`)}
          </li>
        ))}
      </ul>
    </div>
  );
}
